import { motion, useTransform } from "framer-motion";
import React, { CSSProperties, JSX, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useMobileTabBarVisible } from "@core/routing/path";
import { useIsMobile, useScrollDelta } from "@utils/hooks";
import { mobileOnly } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { BackButton } from "@ui/components/navigation/backButton";

interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
	header?: string | JSX.Element;
	renderLeft?: JSX.Element;
	backText?: string;
	backPath?: string;
	backAction?: () => void;
	containerStyle?: CSSProperties;
	headerStyle?: CSSProperties;
	onScrollStarted?: (value: boolean) => void;
	noMarginTop?: boolean;
	noPaddingTop?: boolean;
}
export const Page: React.FC<PageProps> = ({
	header,
	backPath,
	backText,
	backAction,
	renderLeft,
	children,
	containerStyle,
	headerStyle,
	onScrollStarted,
	noMarginTop,
	...htmlProps
}) => {
	const isMobile = useIsMobile();
	const mobileTabBarVisible = useMobileTabBarVisible();
	const delta = useScrollDelta(75);
	const headerOffset = useTransform(delta, (v) => -v);
	const [scrolling, setScrolling] = useState(false);

	useEffect(() => {
		const listener = (): void => {
			setScrolling(window.scrollY > 15);
			onScrollStarted?.(window.scrollY > 15);
		};
		document.addEventListener("scroll", listener, true);
		return () => document.removeEventListener("scroll", listener, true);
	}, []);

	const displayHeader = isMobile && !mobileTabBarVisible;
	const hasOffset = mobileTabBarVisible || !isMobile;

	return (
		<Container style={containerStyle} hasOffset={hasOffset}>
			{displayHeader && (
				<Header style={{ ...headerStyle, y: headerOffset }} scrolled={scrolling}>
					<BackButton text={backText} path={backPath} onClick={backAction} />
					{header}
				</Header>
			)}
			<div {...htmlProps}>{children}</div>
		</Container>
	);
};

export const FullPage: React.FC<PageProps> = ({
	header,
	backPath,
	backText,
	backAction,
	renderLeft,
	children,
	containerStyle,
	headerStyle,
	onScrollStarted,
	noMarginTop,
	noPaddingTop,
	...htmlProps
}) => {
	const isMobile = useIsMobile();
	const mobileTabBarVisible = useMobileTabBarVisible();
	const delta = useScrollDelta(75);
	const headerOffset = useTransform(delta, (v) => -v);
	const [scrolling, setScrolling] = useState(false);

	useEffect(() => {
		const listener = (): void => {
			setScrolling(window.scrollY > 15);
			onScrollStarted?.(window.scrollY > 15);
		};
		document.addEventListener("scroll", listener, true);
		return () => document.removeEventListener("scroll", listener, true);
	}, []);

	const displayHeader = isMobile && !mobileTabBarVisible;
	const hasOffset = mobileTabBarVisible || !isMobile;

	return (
		<FullContainer noPaddingTop={noPaddingTop} noMarginTop={noMarginTop} style={containerStyle} hasOffset={hasOffset}>
			{displayHeader && (
				<Header style={{ ...headerStyle, y: headerOffset }} scrolled={scrolling}>
					<BackButton text={backText} path={backPath} onClick={backAction} />
					{header}
				</Header>
			)}
			<div {...htmlProps}>{children}</div>
		</FullContainer>
	);
};

export const NoMaxWidthPage: React.FC<PageProps> = ({
	header,
	backPath,
	backText,
	backAction,
	renderLeft,
	children,
	containerStyle,
	headerStyle,
	onScrollStarted,
	...htmlProps
}) => {
	const isMobile = useIsMobile();
	const mobileTabBarVisible = useMobileTabBarVisible();
	const delta = useScrollDelta(75);
	const headerOffset = useTransform(delta, (v) => -v);
	const [scrolling, setScrolling] = useState(false);

	useEffect(() => {
		const listener = (): void => {
			setScrolling(window.scrollY > 15);
			onScrollStarted?.(window.scrollY > 15);
		};
		document.addEventListener("scroll", listener, true);
		return () => document.removeEventListener("scroll", listener, true);
	}, []);

	const displayHeader = isMobile && !mobileTabBarVisible;
	const hasOffset = mobileTabBarVisible || !isMobile;

	return (
		<NoMaxWidthContainer style={containerStyle} hasOffset={hasOffset}>
			{displayHeader && (
				<Header style={{ ...headerStyle, y: headerOffset }} scrolled={scrolling}>
					<BackButton text={backText} path={backPath} onClick={backAction} />
					{header}
				</Header>
			)}
			<div {...htmlProps}>{children}</div>
		</NoMaxWidthContainer>
	);
};

const NoMaxWidthContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "hasOffset",
})<{ hasOffset: boolean }>`
	flex: 1;
	margin-top: ${({ hasOffset }) => (hasOffset ? 90 : 72)}px;
	padding: 40px 0 120px 0;
	position: relative;
`;

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "hasOffset",
})<{ hasOffset: boolean }>`
	flex: 1;
	margin-top: ${({ hasOffset }) => (hasOffset ? 90 : 72)}px;
	padding: 40px 0 120px 0;
	position: relative;

	max-width: 1140px;
	@media (max-width: 1139px) {
		max-width: 90vw;
	}
	@media (min-width: 1440px) {
		max-width: 1285px;
	}
	@media (max-width: 1440px) {
		margin-left: 10px;
		margin-right: 10px;
	}

	@media (max-width: 900px) {
		width: 100vw;
		max-width: 100vw;
		margin-left: 0;
		margin-right: 0;
		padding: 10px 15px 120px;
	}
`;

const Header = styled(motion.div)<{ scrolled: boolean }>`
	z-index: ${Z.toolbar};
	display: flex;
	align-items: center;
	padding: 15px;
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	background-color: ${({ theme }) => theme.colors.bodyBackground};

	${({ scrolled }) =>
		scrolled &&
		css`
			box-shadow: 0 2px 54px 0 rgba(0, 0, 0, 0.26);
		`};
	${mobileOnly()}
	justify-content: space-between;
	font-size: 1.25rem;
	font-weight: bold;
	width: 100%;
`;

const FullContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => !["hasOffset", "noMarginTop", "noPaddingTop"].includes(prop),
})<{ hasOffset: boolean; noMarginTop?: boolean; noPaddingTop?: boolean }>`
	flex: 1;
	margin-top: ${({ hasOffset, noMarginTop }) => (noMarginTop ? undefined : hasOffset ? 90 : 72)}px;
	padding: ${({ noPaddingTop }) => (noPaddingTop ? 0 : "40px 0 120px 0")};
	position: relative;
	max-width: 1650px;

	@media (max-width: 1900px) {
		margin-left: ${({ noMarginTop }) => (noMarginTop ? undefined : "80px")};
		margin-right: ${({ noMarginTop }) => (noMarginTop ? undefined : "80px")};
	}

	@media (max-width: 900px) {
		width: 100vw;
		max-width: 100vw;
		margin-left: 0;
		margin-right: 0;
		padding: 10px 15px 10px;
	}
`;
